import { apiClient } from '../client';

/**
 * Client for `/api/eos/one-on-ones/*`. Stays in lockstep with
 * `server/src/routes/eos/oneOnOnes.routes.ts` (Stream B Wave 3, ADR-023).
 *
 * Privacy (locked): a 1-on-1 is visible to its two participants
 * (superadmins see all). Notes and ratings are split per side — each
 * participant writes only their OWN fields; the manager additionally
 * owns scheduling (`scheduledFor` / `kind`).
 */

export type OneOnOneKind = 'check_in' | 'quarterly' | 'annual';
export type OneOnOneStatus = 'scheduled' | 'completed' | 'canceled';

/** How the manager rates a core value in a quarterly/annual conversation. */
export type CoreValueRating = 'plus' | 'plus_minus' | 'minus';

/**
 * The manager-captured assessment of a quarterly/annual conversation
 * (EOS 5-5-5). Labels are SNAPSHOTTED from {@link QuarterlyContext} when
 * the manager fills the agenda, so the record stays stable if the V/TO or
 * rocks change later. Mirrors the server's `conversationAgendaSchema`.
 */
export interface ConversationAgenda {
  coreValues?: { label: string; rating?: CoreValueRating | null; note?: string | null }[];
  roles?: {
    label: string;
    get?: boolean | null;
    want?: boolean | null;
    capacity?: boolean | null;
    note?: string | null;
  }[];
  rocks?: { label: string; onTrack?: boolean | null; note?: string | null }[];
  /** "Are we on the same page?" outcome — int 1..10. */
  samePageRating?: number | null;
  summary?: string | null;
}

/** One yes/no tally within a People Analyzer GWC column. */
export interface GwcTally {
  yes: number;
  no: number;
}

/**
 * The DERIVED, read-only context for a quarterly/annual conversation —
 * assembled live by the server and never stored. Feeds the 5-5-5 panel.
 */
export interface QuarterlyContext {
  /** ISO date (YYYY-MM-DD). */
  quarterStart: string;
  /** e.g. "Q3 2026". */
  quarterLabel: string;
  /** The org's current V/TO core values. */
  coreValues: string[];
  /** The report's current seats. */
  roles: { seatName: string; purpose: string | null; responsibilities: string[] }[];
  /** Latest closed People Analyzer round tally for the report, or null. */
  gwc: {
    roundLabel: string;
    getsIt: GwcTally;
    wantsIt: GwcTally;
    capacity: GwcTally;
  } | null;
  /** The report's current-quarter rocks. */
  rocks: { id: string; title: string; status: string; type: string }[];
}

export interface OneOnOne {
  id: string;
  organizationId: string;
  managerMembershipId: string;
  reportMembershipId: string;
  kind: OneOnOneKind;
  scheduledFor: string;
  status: OneOnOneStatus;
  completedAt: string | null;
  managerNotes: string | null;
  reportNotes: string | null;
  managerRating: number | null;
  reportRating: number | null;
  /** Manager-owned structured agenda for quarterly/annual conversations. */
  agendaJson: ConversationAgenda | null;
  peopleAnalyzerScoreId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** List/create/update include both participants' display names. */
export type OneOnOneWithParticipants = OneOnOne & {
  managerMembership: { id: string; user: { firstName: string; lastName: string } };
  reportMembership: { id: string; user: { firstName: string; lastName: string } };
};

export interface ListOneOnOnesQuery {
  /** ISO datetime — inclusive lower bound on `scheduledFor`. */
  from?: string;
  /** ISO datetime — inclusive upper bound on `scheduledFor`. */
  to?: string;
  /** Superadmin-only narrowing to one participant. */
  withMembershipId?: string;
}

export async function listOneOnOnes(q: ListOneOnOnesQuery = {}): Promise<OneOnOneWithParticipants[]> {
  const params = new URLSearchParams();
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  if (q.withMembershipId) params.set('withMembershipId', q.withMembershipId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: OneOnOneWithParticipants[] }>(`/api/eos/one-on-ones${query}`);
  return res.data.data;
}

export interface CreateOneOnOneRequest {
  reportMembershipId: string;
  /** Superadmin-only — schedule on behalf of another manager. */
  managerMembershipId?: string;
  kind?: OneOnOneKind;
  /** ISO datetime with offset. */
  scheduledFor: string;
}

/** Department leads + superadmins; the caller becomes the manager. */
export async function createOneOnOne(body: CreateOneOnOneRequest): Promise<OneOnOneWithParticipants> {
  const res = await apiClient.post<{ data: OneOnOneWithParticipants }>('/api/eos/one-on-ones', body);
  return res.data.data;
}

export interface UpdateOneOnOneRequest {
  /** Manager-only. ISO datetime with offset. */
  scheduledFor?: string;
  /** Manager-only. */
  kind?: OneOnOneKind;
  managerNotes?: string | null;
  managerRating?: number | null;
  reportNotes?: string | null;
  reportRating?: number | null;
  /** Manager-only — the structured 5-5-5 assessment. */
  agendaJson?: ConversationAgenda;
}

/**
 * Participants only; the server rejects fields that aren't yours with
 * `field_not_yours` — send ONLY your own side's fields.
 */
export async function updateOneOnOne(
  id: string,
  body: UpdateOneOnOneRequest,
): Promise<OneOnOneWithParticipants> {
  const res = await apiClient.put<{ data: OneOnOneWithParticipants }>(
    `/api/eos/one-on-ones/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

/** Either participant. Idempotent — no participant joins on the response. */
export async function completeOneOnOne(id: string): Promise<OneOnOne> {
  const res = await apiClient.post<{ data: OneOnOne }>(
    `/api/eos/one-on-ones/${encodeURIComponent(id)}/complete`,
  );
  return res.data.data;
}

/** Soft delete — sets `status: canceled`. Participants + superadmins. */
export async function cancelOneOnOne(id: string): Promise<void> {
  await apiClient.delete(`/api/eos/one-on-ones/${encodeURIComponent(id)}`);
}

/**
 * The derived 5-5-5 context (core values / roles+GWC / rocks) for a
 * quarterly or annual conversation. Same visibility gate as the read:
 * participants + superadmins. `quarterStart` (YYYY-MM-DD) defaults to the
 * current quarter server-side.
 */
export async function getQuarterlyContext(id: string, quarterStart?: string): Promise<QuarterlyContext> {
  const query = quarterStart ? `?quarterStart=${encodeURIComponent(quarterStart)}` : '';
  const res = await apiClient.get<{ data: QuarterlyContext }>(
    `/api/eos/one-on-ones/${encodeURIComponent(id)}/context${query}`,
  );
  return res.data.data;
}
